import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './databases.css';
import './DatabaseWorkspace.css';
import Navbar from '../../shared/navbar.js';
import PropertyCard from './properties_database.js';
import TenantCard from './tenants_database.js';
import ApplicantCard from './ApplicantDatabase.js';
import StaffCard from './staff_database.js';
import VendorsCard from './vendors_database.js';
import InvoicesCard from './invoices_database.js';
import CustomDatabase from './CustomDatabase.js';

const BUILT_IN_DATABASES = [
  { id: 'properties', label: 'Properties', icon: '🏢' },
  { id: 'tenants', label: 'Tenants', icon: '👤' },
  { id: 'applications', label: 'Applications', icon: '📋' },
  { id: 'staff', label: 'Staff', icon: '👥' },
  { id: 'vendors', label: 'Vendors', icon: '🏪' },
  { id: 'invoices', label: 'Invoices', icon: '🧾' },
];

const COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'select', label: 'Select' },
];

const ICONS = ['📁','📊','📋','📦','🏷️','💼','🗂️','📌','🔖','🧾','🛒','🎯','📈','🔧','💰','🏠','👥','📞','✉️','⭐'];

function DatabasesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'properties');
  const [customDbs, setCustomDbs] = useState([]);

  // Create-database modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDbName, setNewDbName] = useState('');
  const [newDbIcon, setNewDbIcon] = useState('📁');
  const [newDbColumns, setNewDbColumns] = useState([{ name: '', type: 'text' }]);
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('username');
    if (!storedUser) {
      navigate('/');
      return;
    }
    fetchCustomDbs();
  }, [navigate]);

  const fetchCustomDbs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/custom-databases`);
      if (res.ok) {
        const data = await res.json();
        setCustomDbs(data);
      }
    } catch (err) {
      console.error('Failed to load custom databases:', err);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Merge built-in + custom for sidebar
  const allDatabases = [
    ...BUILT_IN_DATABASES,
    ...customDbs.map(db => ({ id: db.table_name, label: db.display_name, icon: db.icon, custom: true, columns: db.columns })),
  ];

  const activeDb = allDatabases.find(d => d.id === activeTab) || allDatabases[0];

  // Column management in modal
  const addColumn = () => setNewDbColumns(prev => [...prev, { name: '', type: 'text' }]);
  const removeColumn = (idx) => setNewDbColumns(prev => prev.filter((_, i) => i !== idx));
  const updateColumn = (idx, field, value) => {
    setNewDbColumns(prev => prev.map((col, i) => i === idx ? { ...col, [field]: value } : col));
  };

  const handleCreateDatabase = async () => {
    setCreateError('');
    const name = newDbName.trim();
    if (!name) { setCreateError('Database name is required'); return; }
    const validCols = newDbColumns.filter(c => c.name.trim());
    if (validCols.length === 0) { setCreateError('At least one column with a name is required'); return; }

    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/custom-databases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: name, icon: newDbIcon, columns: validCols }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create');

      // Reset modal
      setShowCreateModal(false);
      setNewDbName('');
      setNewDbIcon('📁');
      setNewDbColumns([{ name: '', type: 'text' }]);

      // Refresh and switch to new DB
      await fetchCustomDbs();
      handleTabChange(data.table_name);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDatabase = async (dbId, tableName) => {
    if (!window.confirm('Delete this entire database and all its data? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/custom-databases/${dbId}`, { method: 'DELETE' });
      if (res.ok) {
        await fetchCustomDbs();
        if (activeTab === tableName) handleTabChange('properties');
      }
    } catch (err) {
      console.error('Failed to delete database:', err);
    }
  };

  return (
    <>
      <Navbar selected="Databases" />
      <div className="db-workspace">
        {/* ── Left Sidebar ── */}
        <aside className="db-sidebar">
          <div className="db-sidebar-header">
            <span className="db-sidebar-title">Databases</span>
            <button className="db-sidebar-add" title="Create Database" onClick={() => setShowCreateModal(true)}>+</button>
          </div>
          <div className="db-sidebar-list">
            {allDatabases.map(db => (
              <button
                key={db.id}
                className={`db-sidebar-item ${activeTab === db.id ? 'active' : ''}`}
                onClick={() => handleTabChange(db.id)}
              >
                <span className="db-sidebar-item-icon">{db.icon}</span>
                <span className="db-sidebar-item-name">{db.label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* ── Main Panel ── */}
        <main className="db-main">
          <div className="db-main-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h1 className="db-main-title">{activeDb.icon} {activeDb.label}</h1>
              {activeDb.custom && (
                <button
                  className="notion-action"
                  style={{ color: '#dc3545', fontSize: '0.75rem' }}
                  onClick={() => {
                    const meta = customDbs.find(d => d.table_name === activeDb.id);
                    if (meta) handleDeleteDatabase(meta.id, meta.table_name);
                  }}
                >
                  Delete Database
                </button>
              )}
            </div>
          </div>

          <div className="db-content">
            {activeTab === 'properties' && <PropertyCard />}
            {activeTab === 'tenants' && <TenantCard />}
            {activeTab === 'applications' && <ApplicantCard />}
            {activeTab === 'staff' && <StaffCard />}
            {activeTab === 'vendors' && <VendorsCard />}
            {activeTab === 'invoices' && <InvoicesCard />}
            {activeDb.custom && <CustomDatabase tableName={activeDb.id} columns={activeDb.columns} />}
          </div>
        </main>
      </div>

      {/* ── Create Database Modal ── */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" style={{ maxWidth: 560, width: '95%' }} onClick={(e) => e.stopPropagation()}>
            <h2 className="notion-modal-title">Create New Database</h2>

            {createError && <div className="error" style={{ margin: '0 0 16px' }}>{createError}</div>}

            <div className="notion-form">
              {/* Name & Icon */}
              <div className="form-row">
                <label>Database Name *</label>
                <input
                  type="text"
                  value={newDbName}
                  onChange={(e) => setNewDbName(e.target.value)}
                  placeholder="e.g. Vendors, Invoices, Leads..."
                  autoFocus
                />
              </div>

              <div className="form-row">
                <label>Icon</label>
                <div className="db-icon-picker">
                  {ICONS.map(ic => (
                    <button
                      key={ic}
                      type="button"
                      className={`db-icon-option ${newDbIcon === ic ? 'selected' : ''}`}
                      onClick={() => setNewDbIcon(ic)}
                    >
                      {ic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Columns */}
              <div className="form-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <label style={{ marginBottom: 8 }}>Columns</label>
                <div className="db-columns-list">
                  {newDbColumns.map((col, idx) => (
                    <div key={idx} className="db-column-row">
                      <input
                        type="text"
                        value={col.name}
                        onChange={(e) => updateColumn(idx, 'name', e.target.value)}
                        placeholder="Column name"
                        className="db-column-name-input"
                      />
                      <select
                        value={col.type}
                        onChange={(e) => updateColumn(idx, 'type', e.target.value)}
                        className="db-column-type-select"
                      >
                        {COLUMN_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      {newDbColumns.length > 1 && (
                        <button type="button" className="db-column-remove" onClick={() => removeColumn(idx)}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" className="db-add-column-btn" onClick={addColumn}>+ Add Column</button>
              </div>

              <div className="form-actions">
                <button className="notion-button notion-button-primary" onClick={handleCreateDatabase} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Database'}
                </button>
                <button className="notion-button" onClick={() => setShowCreateModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DatabasesPage;
